import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, User, ChevronLeft, ChevronRight, Briefcase } from "lucide-react";
import { format, addWeeks, subWeeks, startOfWeek, endOfWeek } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";
import { useCompanySettings, isOffersEnabled, isSwapsEnabled } from "@/hooks/useCompanySettings";
import { useIsMobile } from "@/hooks/use-mobile";
import { Database } from "@/integrations/supabase/types";
import { WeekView, ShiftDetailsDialog, CreateSwapDialog, SwapRequestsList, SwapOffersList } from "@/components/schedule";
import { useMyShifts, useOpenShifts, useClaimShift } from "@/hooks/schedule";
import { useMyLeaveOverlay } from "@/hooks/leave";
import { useCompanyTimezone } from "@/hooks/useCompanyTimezone";


type OpenShiftDetails = Database["public"]["CompositeTypes"]["open_shift_details"];

interface Shift {
  id: string;
  profile_id: string | null;
  start_time: string;
  end_time: string;
  note: string | null;
  employee_name: string;
  status?: 'active' | 'open';
  offered_by_user_id?: string | null;
  offered_by_name?: string | null;
  swap_status?: string | null;
}

function MyScheduleContent() {
  const { user, profile } = useAuth();
  const { settings: companySettings } = useCompanySettings();
  const companyTimezone = useCompanyTimezone();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [currentWeek, setCurrentWeek] = useState(new Date());

  // Dialog state management
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [showShiftDetails, setShowShiftDetails] = useState(false);
  const [showCreateSwap, setShowCreateSwap] = useState(false);

  // Helper function to check if offered_by_name contains meaningful content
  const hasValidOfferedByName = (name: string | null | undefined): boolean => {
    return name != null && name.trim().length > 0;
  };

  // Derived values for React Query hooks
  const companyId = profile?.company_id;
  const profileId = profile?.id;
  const profileRole = profile?.role;

  // Calculate week date range for leave overlay
  const weekStart = useMemo(() => format(startOfWeek(currentWeek, { weekStartsOn: 0 }), 'yyyy-MM-dd'), [currentWeek]);
  const weekEnd = useMemo(() => format(endOfWeek(currentWeek, { weekStartsOn: 0 }), 'yyyy-MM-dd'), [currentWeek]);

  // React Query hooks for data fetching
  const { 
    data: shifts = [], 
    isLoading: myShiftsLoading,
    error: myShiftsError 
  } = useMyShifts(profileId, currentWeek, profile);

  // Fetch leave overlays for the current user
  const { data: leaveOverlays = [] } = useMyLeaveOverlay(profileId, weekStart, weekEnd);

  
  const { 
    data: openShiftsData, 
    isLoading: openShiftsLoading,
    error: openShiftsError 
  } = useOpenShifts(companyId, currentWeek, profileRole, companySettings);
  
  // Type guard and safe access for openShifts
  const openShifts = Array.isArray(openShiftsData) ? openShiftsData : [];
  
  // React Query mutation for claiming shifts
  const claimShiftMutation = useClaimShift(profileId, profile, currentWeek, companyId);
  
  const loading = myShiftsLoading || openShiftsLoading;

  // Error logging (React Query handles retries automatically)
  useEffect(() => {
    if (myShiftsError) {
      console.error('Error fetching my shifts:', myShiftsError);
    }
  }, [myShiftsError]);

  useEffect(() => {
    if (openShiftsError) {
      console.error('Error fetching open shifts:', openShiftsError);
    }
  }, [openShiftsError]);

  // Auth check effect (no data fetching)
  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
  }, [user, navigate]);

  const handleClaimShift = (shiftId: string) => {
    claimShiftMutation.mutate(shiftId);
  };

  // Shift click handler - opens shift details dialog
  const handleShiftClick = (shift: any) => {
    // Convert shift data to our Shift interface
    const shiftData: Shift = {
      id: shift.id,
      profile_id: shift.profile_id,
      start_time: shift.start_time,
      end_time: shift.end_time,
      note: shift.note,
      employee_name: shift.employee_name || `${profile?.first_name} ${profile?.last_name}` || 'Unknown',
      status: shift.status,
      offered_by_user_id: shift.offered_by_user_id,
      offered_by_name: shift.offered_by_name,
      swap_status: shift.swap_status,
    };
    
    setSelectedShift(shiftData);
    setShowShiftDetails(true);
  };

  // Can't work handler - opens swap creation dialog
  const handleCantWork = (shift: Shift) => {
    setSelectedShift(shift);
    setShowShiftDetails(false);
    setShowCreateSwap(true);
  };

  // Close all dialogs
  const handleCloseDialogs = () => {
    setShowShiftDetails(false);
    setShowCreateSwap(false);
    setSelectedShift(null);
  };


  const getTotalHoursThisWeek = () => {
    return shifts.reduce((total, shift) => {
      const start = new Date(shift.start_time);
      const end = new Date(shift.end_time);
      const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      return total + hours;
    }, 0);
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-4 md:py-8 space-y-4 md:space-y-6 overflow-x-hidden">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <User className="h-5 w-5 md:h-6 md:w-6" />
          <h1 className="text-xl md:text-2xl font-bold">My Schedule</h1>
        </div>
        {isMobile ? (
          <div className="flex items-center gap-1">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCurrentWeek(new Date())}
              className="px-2"
            >
              Today
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}>
              Previous Week
            </Button>
            <Button variant="outline" onClick={() => setCurrentWeek(new Date())}>
              This Week
            </Button>
            <Button variant="outline" onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}>
              Next Week
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs md:text-sm font-medium">Total Hours This Week</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold">{getTotalHoursThisWeek().toFixed(1)}</div>
            <p className="text-xs text-muted-foreground">hours scheduled</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs md:text-sm font-medium">Shifts This Week</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold">{shifts.length}</div>
            <p className="text-xs text-muted-foreground">total shifts</p>
          </CardContent>
        </Card>
      </div>

      <WeekView
        currentWeek={currentWeek}
        onWeekChange={setCurrentWeek}
        shifts={shifts}
        onTimeSlotClick={() => {}} // No-op for view-only mode
        onShiftsUpdated={() => {}} // No-op for view-only mode
        profiles={[]} // Empty array since we don't need profile management
        onShiftClick={handleShiftClick} // Enable shift clicking for details
        onCreateShift={undefined} // Disable shift creation
        leaveOverlays={leaveOverlays}
        isEmployeeView={true}
        highlightShiftDuration={true}
        companyTimezone={companyTimezone}
      />

      {shifts.length === 0 && (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">No shifts scheduled</h3>
              <p>You don't have any shifts scheduled for this week.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Open Shifts Section - All authenticated users can claim when offers are enabled */}
      {isOffersEnabled(companySettings) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Open Shifts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {openShifts.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No open shifts</h3>
                <p>There are no open shifts available for this week.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {openShifts.map(shift => (
                  <div
                    key={shift.id}
                    className="flex items-center justify-between p-4 border border-dashed border-muted-foreground/30 bg-muted/40 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <div className="font-medium">
                          {format(new Date(shift.start_time), 'EEEE, MMM d')}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          Open
                        </Badge>
                        {hasValidOfferedByName(shift.offered_by_name) && (
                          <Badge variant="secondary" className="text-xs">
                            Offered by {shift.offered_by_name.trim()}
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {format(new Date(shift.start_time), 'h:mm a')} - 
                        {format(new Date(shift.end_time), 'h:mm a')}
                        <span className="ml-2">
                          ({((new Date(shift.end_time).getTime() - new Date(shift.start_time).getTime()) / (1000 * 60 * 60)).toFixed(1)} hours)
                        </span>
                      </div>
                      {shift.note && (
                        <div className="text-sm text-muted-foreground mt-1">
                          Note: {shift.note}
                        </div>
                      )}
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm"
                          disabled={claimShiftMutation.isPending}
                        >
                          {claimShiftMutation.isPending ? 'Claiming...' : 'Claim Shift'}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Claim Shift</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to claim this shift for {format(new Date(shift.start_time), 'EEEE, MMM d')} from {format(new Date(shift.start_time), 'h:mm a')} to {format(new Date(shift.end_time), 'h:mm a')}?
                            {shift.note && (
                              <div className="mt-2 p-2 bg-muted rounded text-sm">
                                <strong>Note:</strong> {shift.note}
                              </div>
                            )}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleClaimShift(shift.id)}
                            disabled={claimShiftMutation.isPending}
                          >
                            {claimShiftMutation.isPending ? 'Claiming...' : 'Claim Shift'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Swap Requests and Offers - Only show if swaps are enabled */}
      {isSwapsEnabled(companySettings) && (
        <>
          <SwapRequestsList currentWeek={currentWeek} />
          <SwapOffersList currentWeek={currentWeek} />
        </>
      )}

      {/* Swap-related dialogs */}
      <ShiftDetailsDialog
        shift={selectedShift}
        open={showShiftDetails}
        onOpenChange={setShowShiftDetails}
        onCantWork={handleCantWork}
      />

      <CreateSwapDialog
        shift={selectedShift}
        open={showCreateSwap}
        onOpenChange={setShowCreateSwap}
        currentWeek={currentWeek}
      />
    </div>
  );
}

/**
 * MySchedule page — realtime for open shifts is handled by
 * CompanyRealtimeProvider at the AuthenticatedRoutesLayout level.
 */
export default function MySchedule() {
  return <MyScheduleContent />;
}
